import React, { useEffect, useState } from "react";
import {
    FaArrowLeft,
    FaArrowRight,
    FaCheckCircle,
    FaEnvelope,
    FaExclamationCircle,
    FaGlobe,
    FaPlus,
    FaRedo,
    FaSearch,
    FaTrash,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import HeaderMainPage from "../../header/HeaderMainPage";

const PAGE_SIZE = 10;

const getStoredToken = () => localStorage.getItem("access_token") || "";

const parseJwtPayload = (token) => {
    if (!token) {
        return null;
    }

    const normalizedToken = token.startsWith("Bearer ") ? token.slice(7) : token;
    const parts = normalizedToken.split(".");

    if (parts.length < 2) {
        return null;
    }

    try {
        const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
        const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
        const payload = window.atob(padded);
        return JSON.parse(payload);
    } catch (error) {
        return null;
    }
};

const getRequestUserId = () => {
    const directUserId =
        localStorage.getItem("user_id") || localStorage.getItem("request_user_id") || "";

    if (directUserId) {
        return directUserId;
    }

    const jwtPayload = parseJwtPayload(getStoredToken());
    const tokenUserId =
        jwtPayload?.user_id ||
        jwtPayload?.sub ||
        jwtPayload?.id ||
        jwtPayload?.user?.user_id ||
        "";

    if (tokenUserId) {
        return String(tokenUserId);
    }

    try {
        const userData = JSON.parse(localStorage.getItem("user_data") || "null");
        return userData?.user_id || "";
    } catch (error) {
        return "";
    }
};

const getApiHeaders = (extraHeaders = {}) => {
    const token = getStoredToken();
    const requestUserId = getRequestUserId();

    return {
        "Content-Type": "application/json",
        ...(token ? { Authorization: token } : {}),
        ...(requestUserId ? { request_user_id: requestUserId } : {}),
        ...extraHeaders,
    };
};

const toArray = (value) => {
    if (Array.isArray(value)) {
        return value;
    }

    if (Array.isArray(value?.items)) {
        return value.items;
    }

    if (Array.isArray(value?.data)) {
        return value.data;
    }

    if (Array.isArray(value?.accounts)) {
        return value.accounts;
    }

    if (Array.isArray(value?.domains)) {
        return value.domains;
    }

    return [];
};

const uniqueBy = (items, keySelector) => {
    const seen = new Set();

    return items.filter((item) => {
        const key = keySelector(item);

        if (!key || seen.has(key)) {
            return false;
        }

        seen.add(key);
        return true;
    });
};

const normalizeDomains = (payload) => {
    const domainSource = payload?.domains ?? payload?.metadata ?? payload;
    const rawItems = toArray(domainSource);

    const parsedDomains = rawItems
        .map((item, index) => {
            if (typeof item === "string") {
                return {
                    id: item,
                    domain: item,
                    provider: "MAILDOSO",
                    status: "connected",
                };
            }

            const domain =
                item?.domain ||
                item?.name ||
                item?.domain_name ||
                item?.hostname ||
                item?.dns_domain ||
                "";

            return {
                id: item?.id || item?.domain_id || domain || `domain-${index}`,
                domain,
                provider: item?.provider || item?.vendor || "MAILDOSO",
                status: item?.status || item?.state || "connected",
            };
        })
        .filter((item) => item.domain);

    return uniqueBy(parsedDomains, (item) => item.domain.toLowerCase());
};

const normalizeForwardingAccounts = (payload) => {
    const forwardingSource = payload?.metadata ?? payload;
    const rawItems = toArray(forwardingSource);

    return rawItems
        .map((item, index) => {
            const accountId =
                item?.id || item?.account_id || item?.forwarding_account_id || `forward-${index}`;
            const email = item?.email_account || item?.email || item?.address || "";
            const label = item?.name || item?.display_name || email || `Forwarding ${index + 1}`;

            return {
                id: String(accountId),
                label,
                email,
            };
        })
        .filter((item) => item.id);
};

const normalizeEmailAccounts = (payload) => {
    const emailSource = payload?.metadata ?? payload?.email_accounts ?? payload;
    const rawItems = toArray(emailSource);

    return rawItems
        .map((item, index) => {
            if (typeof item === "string") {
                const derivedDomain = item.includes("@") ? item.split("@")[1] : "";

                return {
                    id: `account-${index}`,
                    email: item,
                    domain: derivedDomain,
                    firstName: "",
                    lastName: "",
                    isActive: true,
                    provider: "maildoso",
                    googleScore: "",
                    forwardingAccountId: "",
                };
            }

            const email = item?.email_account || item?.sender_address || item?.email || "";
            const domain = item?.domain || (email.includes("@") ? email.split("@")[1] : "");

            return {
                id: item?.account_id || item?.id || `account-${index}`,
                email,
                domain,
                firstName: item?.first_name || "",
                lastName: item?.last_name || "",
                isActive: Boolean(item?.is_active ?? true),
                provider: item?.provider || "maildoso",
                reputationTest: item?.reputation_test || "",
                googleScore: String(item?.reputation_test?.google_score || "").toLowerCase(),
                forwardingAccountId: item?.forwarding_account_id ? String(item.forwarding_account_id) : "",
            };
        })
        .filter((item) => item.email);
};

const getGoogleScoreBadgeClass = (score) => {
    if (score === "high") {
        return "border-emerald-200 bg-emerald-50 text-emerald-700";
    }
    if (score === "medium") {
        return "border-amber-200 bg-amber-50 text-amber-700";
    }
    if (score === "low") {
        return "border-rose-200 bg-rose-50 text-rose-700";
    }
    return "border-slate-200 bg-white text-slate-500";
};

const FormInput = ({ label, children, hint }) => (
    <label className="block">
        <span className="mb-2 block text-sm font-medium text-slate-700">{label}</span>
        {children}
        {hint ? <span className="mt-1 block text-xs text-slate-500">{hint}</span> : null}
    </label>
);

const STEPS = [
    { number: 1, label: "Domain Setup" },
    { number: 2, label: "Email Accounts" },
    { number: 3, label: "Confirmation" },
];

const StepIndicator = ({ currentStep }) => (
    <div className="flex items-center justify-center">
        {STEPS.map((step, index) => (
            <React.Fragment key={step.number}>
                <div
                    className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
                        currentStep === step.number
                            ? "bg-slate-900 text-white"
                            : currentStep > step.number
                            ? "bg-emerald-100 text-emerald-700"
                            : "bg-slate-100 text-slate-400"
                    }`}
                >
                    <span
                        className={`flex h-5 w-5 items-center justify-center rounded-full text-xs font-bold ${
                            currentStep === step.number
                                ? "bg-white text-slate-900"
                                : currentStep > step.number
                                ? "bg-emerald-500 text-white"
                                : "bg-slate-300 text-slate-600"
                        }`}
                    >
                        {currentStep > step.number ? "✓" : step.number}
                    </span>
                    {step.label}
                </div>
                {index < STEPS.length - 1 && (
                    <div
                        className={`mx-1 h-0.5 w-10 transition-colors ${
                            currentStep > step.number ? "bg-emerald-400" : "bg-slate-200"
                        }`}
                    />
                )}
            </React.Fragment>
        ))}
    </div>
);

const DomainEmailPage = () => {
    const API_BASE = process.env.REACT_APP_ROBOMAN_API;
    const navigate = useNavigate();

    const [currentStep, setCurrentStep] = useState(1);
    const [domains, setDomains] = useState([]);
    const [forwardingAccounts, setForwardingAccounts] = useState([]);
    const [emailAccounts, setEmailAccounts] = useState([]);
    const [selectedDomain, setSelectedDomain] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [loadingDomains, setLoadingDomains] = useState(true);
    const [loadingForwardingAccounts, setLoadingForwardingAccounts] = useState(true);
    const [loadingEmailAccounts, setLoadingEmailAccounts] = useState(true);
    const [refreshingAll, setRefreshingAll] = useState(false);
    const [submittingDomain, setSubmittingDomain] = useState(false);
    const [checkingDomain, setCheckingDomain] = useState(false);
    const [submittingEmail, setSubmittingEmail] = useState(false);
    const [deletingAccountId, setDeletingAccountId] = useState("");
    const [actionMessage, setActionMessage] = useState({ type: "", text: "" });
    const [availability, setAvailability] = useState({
        domain: "",
        available: null,
        message: "",
    });
    const [domainForm, setDomainForm] = useState({
        domain: "",
        redirection_url: "",
    });
    const [domainIdea, setDomainIdea] = useState("");
    const [discoveringDomains, setDiscoveringDomains] = useState(false);
    const [suggestedValidDomains, setSuggestedValidDomains] = useState([]);
    const [emailForm, setEmailForm] = useState({
        mailbox_name: "",
        forwarding_account_id: "",
        first_name: "",
        last_name: "",
        picture_url: "",
        provider: "maildoso",
    });

    const fetchDomains = async () => {
        setLoadingDomains(true);

        try {
            const response = await fetch(`${API_BASE}/campaign/get_user_domains/`, {
                method: "GET",
                headers: getApiHeaders(),
            });

            if (!response.ok) {
                throw new Error("Failed to fetch domains.");
            }

            const payload = await response.json();
            const normalizedDomains = normalizeDomains(payload);

            setDomains(normalizedDomains);
            setSelectedDomain((previousDomain) => {
                if (previousDomain && normalizedDomains.some((item) => item.domain === previousDomain)) {
                    return previousDomain;
                }

                return normalizedDomains[0]?.domain || "";
            });
        } catch (error) {
            console.error("Error fetching domains:", error);
            setDomains([]);
            setActionMessage({ type: "error", text: error.message || "Unable to load domains." });
        } finally {
            setLoadingDomains(false);
        }
    };

    const fetchForwardingAccounts = async () => {
        setLoadingForwardingAccounts(true);

        try {
            const response = await fetch(`${API_BASE}/campaign/fetch_forwarding_accounts/`, {
                method: "GET",
                headers: getApiHeaders(),
            });

            if (!response.ok) {
                throw new Error("Failed to fetch forwarding accounts.");
            }

            const payload = await response.json();
            const normalizedAccounts = normalizeForwardingAccounts(payload);
            setForwardingAccounts(normalizedAccounts);
            setEmailForm((previousForm) => ({
                ...previousForm,
                forwarding_account_id:
                    previousForm.forwarding_account_id || normalizedAccounts[0]?.id || "",
            }));
        } catch (error) {
            console.error("Error fetching forwarding accounts:", error);
            setForwardingAccounts([]);
            setActionMessage({
                type: "error",
                text: error.message || "Unable to load forwarding accounts.",
            });
        } finally {
            setLoadingForwardingAccounts(false);
        }
    };

    const fetchEmailAccounts = async (page = currentPage, domain = selectedDomain) => {
        setLoadingEmailAccounts(true);

        try {
            const response = await fetch(
                `${API_BASE}/campaign/fetch_email_accounts/${page}/${PAGE_SIZE}`,
                {
                    method: "GET",
                    headers: getApiHeaders(),
                }
            );

            if (!response.ok) {
                throw new Error("Failed to fetch email accounts.");
            }

            const payload = await response.json();
            setEmailAccounts(normalizeEmailAccounts(payload));
            setCurrentPage(page);
        } catch (error) {
            console.error("Error fetching email accounts:", error);
            setEmailAccounts([]);
            setActionMessage({
                type: "error",
                text: error.message || "Unable to load email accounts.",
            });
        } finally {
            setLoadingEmailAccounts(false);
        }
    };

    const fetchAllEmailAccounts = async () => {
        setLoadingEmailAccounts(true);

        try {
            let page = 1;
            let allAccounts = [];

            while (true) {
                const response = await fetch(
                    `${API_BASE}/campaign/fetch_email_accounts/${page}/${PAGE_SIZE}`,
                    { method: "GET", headers: getApiHeaders() }
                );

                if (!response.ok) {
                    throw new Error("Failed to fetch email accounts.");
                }

                const payload = await response.json();
                const accounts = normalizeEmailAccounts(payload);
                allAccounts = [...allAccounts, ...accounts];

                if (accounts.length < PAGE_SIZE) {
                    break;
                }

                page++;
            }

            setEmailAccounts(allAccounts);
            setCurrentPage(1);
        } catch (error) {
            console.error("Error fetching email accounts:", error);
            setEmailAccounts([]);
            setActionMessage({
                type: "error",
                text: error.message || "Unable to load email accounts.",
            });
        } finally {
            setLoadingEmailAccounts(false);
        }
    };

    const refreshAllData = async () => {
        setRefreshingAll(true);
        await Promise.all([
            fetchDomains(),
            fetchForwardingAccounts(),
            fetchEmailAccounts(1),
        ]);
        setRefreshingAll(false);
    };

    useEffect(() => {
        refreshAllData();
    }, []);

    const handleDomainFormChange = (event) => {
        const { name, value } = event.target;

        setDomainForm((previousForm) => ({
            ...previousForm,
            [name]: value,
        }));

        if (name === "domain") {
            setAvailability({ domain: "", available: null, message: "" });
        }
    };

    const handleEmailFormChange = (event) => {
        const { name, value } = event.target;

        setEmailForm((previousForm) => ({
            ...previousForm,
            [name]: value,
        }));
    };

    const handleDiscoverDomains = async () => {
        const trimmedIdea = domainIdea.trim();

        if (!trimmedIdea) {
            setActionMessage({ type: "error", text: "Enter your idea to discover domain suggestions." });
            return;
        }

        setDiscoveringDomains(true);
        setActionMessage({ type: "", text: "" });

        try {
            const response = await fetch(`${API_BASE}/campaign/check_domain_suggestions/`, {
                method: "POST",
                headers: getApiHeaders(),
                body: JSON.stringify({ domain_text: trimmedIdea }),
            });

            if (!response.ok) {
                throw new Error("Unable to discover domain suggestions.");
            }

            const payload = await response.json();
            const validDomains = Array.isArray(payload?.valid_domains)
                ? payload.valid_domains
                      .map((item) => String(item || "").trim().toLowerCase())
                      .filter(Boolean)
                : [];

            setSuggestedValidDomains([...new Set(validDomains)]);

            if (validDomains.length === 0) {
                setActionMessage({
                    type: "error",
                    text: "No valid domains found for this idea. Try a different prompt.",
                });
            } else {
                setActionMessage({
                    type: "success",
                    text: `${validDomains.length} valid domain suggestion${validDomains.length > 1 ? "s" : ""} found.`,
                });
            }
        } catch (error) {
            console.error("Error discovering domain suggestions:", error);
            setSuggestedValidDomains([]);
            setActionMessage({
                type: "error",
                text: error.message || "Unable to discover domain suggestions.",
            });
        } finally {
            setDiscoveringDomains(false);
        }
    };

    const handleSuggestedDomainSelect = (domain) => {
        setDomainForm((previousForm) => ({
            ...previousForm,
            domain,
        }));
        setAvailability({ domain: "", available: null, message: "" });
    };

    const handleCheckDomain = async () => {
        const trimmedDomain = domainForm.domain.trim().toLowerCase();

        if (!trimmedDomain) {
            setActionMessage({ type: "error", text: "Enter a domain before checking availability." });
            return;
        }

        setCheckingDomain(true);
        setActionMessage({ type: "", text: "" });

        try {
            const response = await fetch(`${API_BASE}/campaign/check_domain_availability/`, {
                method: "POST",
                headers: getApiHeaders(),
                body: JSON.stringify({ domain: trimmedDomain }),
            });

            if (!response.ok) {
                throw new Error("Unable to check domain availability.");
            }

            const payload = await response.json();

            setAvailability({
                domain: trimmedDomain,
                available: Boolean(payload?.available),
                message: payload?.message || "Availability checked.",
            });
            setActionMessage({
                type: payload?.available ? "success" : "error",
                text: payload?.message || "Availability check finished.",
            });
        } catch (error) {
            console.error("Error checking domain availability:", error);
            setAvailability({
                domain: trimmedDomain,
                available: false,
                message: error.message || "Unable to check domain availability.",
            });
            setActionMessage({
                type: "error",
                text: error.message || "Unable to check domain availability.",
            });
        } finally {
            setCheckingDomain(false);
        }
    };

    const handleCreateDomain = async (event) => {
        event.preventDefault();

        const trimmedDomain = domainForm.domain.trim().toLowerCase();
        const trimmedRedirectUrl = domainForm.redirection_url.trim();

        if (!trimmedDomain || !trimmedRedirectUrl) {
            setActionMessage({ type: "error", text: "Domain and redirect URL are required." });
            return;
        }

        if (availability.domain === trimmedDomain && availability.available === false) {
            setActionMessage({
                type: "error",
                text: "This domain is marked unavailable. Choose another domain or recheck it.",
            });
            return;
        }

        setSubmittingDomain(true);
        setActionMessage({ type: "", text: "" });

        try {
            const response = await fetch(`${API_BASE}/campaign/create_user_domain/`, {
                method: "POST",
                headers: getApiHeaders(),
                body: JSON.stringify({
                    domain: trimmedDomain,
                    redirection_url: trimmedRedirectUrl,
                }),
            });

            const payload = await response.json();

            if (!response.ok || payload?.status === "error") {
                throw new Error(payload?.message || "Unable to create domain.");
            }

            setActionMessage({
                type: "success",
                text: payload?.message || `Domain ${trimmedDomain} created successfully.`,
            });
            setDomainForm({ domain: "", redirection_url: "" });
            setAvailability({ domain: "", available: null, message: "" });
            await fetchDomains();
            setSelectedDomain(trimmedDomain);
        } catch (error) {
            console.error("Error creating domain:", error);
            setActionMessage({
                type: "error",
                text: error.message || "Unable to create the domain.",
            });
        } finally {
            setSubmittingDomain(false);
        }
    };

    const handleCreateEmailAccount = async (event) => {
        event.preventDefault();

        const trimmedMailbox = emailForm.mailbox_name.trim().toLowerCase();

        if (!selectedDomain) {
            setActionMessage({ type: "error", text: "Select a domain before adding an email account." });
            return;
        }

        if (
            !trimmedMailbox ||
            !emailForm.first_name.trim() ||
            !emailForm.last_name.trim() ||
            !emailForm.forwarding_account_id
        ) {
            setActionMessage({
                type: "error",
                text: "Mailbox, first name, last name, and forwarding account are required.",
            });
            return;
        }

        setSubmittingEmail(true);
        setActionMessage({ type: "", text: "" });

        try {
            const response = await fetch(`${API_BASE}/campaign/integrate_email_account/`, {
                method: "POST",
                headers: getApiHeaders(),
                body: JSON.stringify({
                    mailbox_name: trimmedMailbox,
                    domain: selectedDomain,
                    forwarding_account_id: emailForm.forwarding_account_id,
                    first_name: emailForm.first_name.trim(),
                    last_name: emailForm.last_name.trim(),
                    picture_url: emailForm.picture_url.trim() || null,
                    provider: emailForm.provider.trim() || null,
                }),
            });

            const payload = await response.json();

            if (!response.ok || payload?.status === "error") {
                throw new Error(payload?.message || "Unable to integrate email account.");
            }

            setActionMessage({
                type: "success",
                text: payload?.message || `Email account ${trimmedMailbox}@${selectedDomain} created successfully.`,
            });
            setEmailForm((previousForm) => ({
                ...previousForm,
                mailbox_name: "",
                first_name: "",
                last_name: "",
                picture_url: "",
            }));
            await fetchAllEmailAccounts();
        } catch (error) {
            console.error("Error integrating email account:", error);
            setActionMessage({
                type: "error",
                text: error.message || "Unable to integrate the email account.",
            });
        } finally {
            setSubmittingEmail(false);
        }
    };

    const handleDeleteEmailAccount = async (accountId, emailAccount) => {
        setDeletingAccountId(String(accountId));
        setActionMessage({ type: "", text: "" });

        try {
            const path = `${API_BASE}/campaign/${accountId}/${emailAccount}/delete/`;

            const response = await fetch(path, {
                method: "DELETE",
                headers: getApiHeaders({ Accept: "application/json" }),
            });

            let deletionPayload = {};

            try {
                deletionPayload = await response.json();
            } catch (error) {
                deletionPayload = {};
            }

            if (!response.ok || deletionPayload?.status === "error") {
                throw new Error(deletionPayload?.message || "Unable to delete email account.");
            }

            setActionMessage({
                type: "success",
                text: deletionPayload?.message || "Email account deleted successfully.",
            });
            await fetchAllEmailAccounts();
        } catch (error) {
            console.error("Error deleting email account:", error);
            setActionMessage({
                type: "error",
                text: error.message || "Unable to delete the email account.",
            });
        } finally {
            setDeletingAccountId("");
        }
    };

    const canGoToNextPage = emailAccounts.length === PAGE_SIZE;
    const selectedForwardingAccount = forwardingAccounts.find(
        (item) => item.id === emailForm.forwarding_account_id
    );
    const previewEmail = emailForm.mailbox_name.trim()
        ? `${emailForm.mailbox_name.trim().toLowerCase()}@${selectedDomain || "your-domain.com"}`
        : "mailbox@your-domain.com";
    const filteredEmailAccounts = selectedDomain
        ? emailAccounts.filter((account) => account.domain?.toLowerCase() === selectedDomain.toLowerCase())
        : emailAccounts;

    const goToStep2 = () => {
        if (!selectedDomain) {
            setActionMessage({ type: "error", text: "Please select or create a domain before continuing." });
            return;
        }
        setActionMessage({ type: "", text: "" });
        setCurrentStep(2);
        fetchAllEmailAccounts();
    };

    const goToStep3 = () => {
        setActionMessage({ type: "", text: "" });
        setCurrentStep(3);
    };

    return (
        <div className="min-h-screen bg-slate-50">
            <HeaderMainPage />

            <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,_rgba(14,165,233,0.14),_transparent_32%),radial-gradient(circle_at_top_right,_rgba(251,191,36,0.18),_transparent_28%),linear-gradient(180deg,_#f8fafc_0%,_#eef2ff_100%)] px-4 py-8 md:px-8">
                <div className="mx-auto max-w-2xl space-y-6">

                    {/* Page header */}
                    <div className="text-center">
                        <p className="text-xs font-semibold uppercase tracking-[0.28em] text-sky-600">
                            Domain Email Integrations
                        </p>
                        <h1 className="mt-2 text-2xl font-semibold text-slate-900">
                            Set up your sending domain & mailboxes
                        </h1>
                    </div>

                    {/* Step indicator */}
                    <StepIndicator currentStep={currentStep} />

                    {/* Global action message */}
                    {actionMessage.text ? (
                        <div
                            className={`flex items-start gap-3 rounded-2xl border px-4 py-3 text-sm ${
                                actionMessage.type === "success"
                                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                    : "border-rose-200 bg-rose-50 text-rose-700"
                            }`}
                        >
                            {actionMessage.type === "success" ? (
                                <FaCheckCircle className="mt-0.5 shrink-0" />
                            ) : (
                                <FaExclamationCircle className="mt-0.5 shrink-0" />
                            )}
                            <span>{actionMessage.text}</span>
                        </div>
                    ) : null}

                    {/* ── STEP 1: Domain ── */}
                    {currentStep === 1 && (
                        <div className="space-y-5">
                            {/* Create new domain */}
                            <div className="rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm">
                                <div className="flex items-center gap-3 mb-5">
                                    <div className="rounded-2xl bg-sky-100 p-3 text-sky-600">
                                        <FaGlobe />
                                    </div>
                                    <div>
                                        <h2 className="font-semibold text-slate-900">Create a new domain</h2>
                                        <p className="text-sm text-slate-500">Check availability then register it.</p>
                                    </div>
                                </div>

                                <form onSubmit={handleCreateDomain} className="space-y-4">
                                    <FormInput label="Describe your idea" hint="Example: AI tools for climate reporting teams.">
                                        <div className="flex flex-col gap-3 sm:flex-row">
                                            <input
                                                type="text"
                                                value={domainIdea}
                                                onChange={(event) => setDomainIdea(event.target.value)}
                                                placeholder="Write your idea and discover domain options"
                                                className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-sky-500 focus:ring-4 focus:ring-sky-100"
                                            />
                                            <button
                                                type="button"
                                                onClick={handleDiscoverDomains}
                                                disabled={discoveringDomains}
                                                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-sky-300 bg-sky-50 px-4 py-3 text-sm font-semibold text-sky-700 transition hover:bg-sky-100 disabled:opacity-60"
                                            >
                                                <FaSearch />
                                                {discoveringDomains ? "Discovering..." : "Discover"}
                                            </button>
                                        </div>
                                    </FormInput>

                                    {suggestedValidDomains.length > 0 ? (
                                        <div className="rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3">
                                            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-sky-700">
                                                Valid domain suggestions
                                            </p>
                                            <div className="flex flex-wrap gap-2">
                                                {suggestedValidDomains.map((domain) => {
                                                    const isSelected = domainForm.domain.trim().toLowerCase() === domain;

                                                    return (
                                                        <button
                                                            key={domain}
                                                            type="button"
                                                            onClick={() => handleSuggestedDomainSelect(domain)}
                                                            className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                                                                isSelected
                                                                    ? "border-sky-700 bg-sky-700 text-white"
                                                                    : "border-sky-300 bg-white text-sky-700 hover:border-sky-500"
                                                            }`}
                                                        >
                                                            {domain}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ) : null}

                                    <FormInput label="Domain" hint="Use the exact domain you want to send from.">
                                        <input
                                            type="text"
                                            name="domain"
                                            value={domainForm.domain}
                                            onChange={handleDomainFormChange}
                                            placeholder="mail.yourcompany.com"
                                            className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-sky-500 focus:ring-4 focus:ring-sky-100"
                                        />
                                    </FormInput>
                                    <FormInput label="Redirection URL" hint="Passed to the create domain API as redirection_url.">
                                        <input
                                            type="url"
                                            name="redirection_url"
                                            value={domainForm.redirection_url}
                                            onChange={handleDomainFormChange}
                                            placeholder="https://yourcompany.com"
                                            className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-sky-500 focus:ring-4 focus:ring-sky-100"
                                        />
                                    </FormInput>

                                    {availability.message ? (
                                        <div className={`rounded-2xl border px-4 py-3 text-sm ${availability.available ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-amber-200 bg-amber-50 text-amber-700"}`}>
                                            {availability.message}
                                        </div>
                                    ) : null}

                                    <div className="flex gap-3">
                                        <button
                                            type="button"
                                            onClick={handleCheckDomain}
                                            disabled={checkingDomain}
                                            className="inline-flex flex-1 items-center justify-center gap-2 rounded-2xl border border-slate-300 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-sky-300 hover:text-sky-700 disabled:opacity-60"
                                        >
                                            <FaCheckCircle />
                                            {checkingDomain ? "Checking..." : "Check availability"}
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={submittingDomain}
                                            className="inline-flex flex-1 items-center justify-center gap-2 rounded-2xl bg-sky-600 px-4 py-3 text-sm font-semibold text-white transition hover:bg-sky-700 disabled:opacity-60"
                                        >
                                            <FaPlus />
                                            {submittingDomain ? "Creating..." : "Create domain"}
                                        </button>
                                    </div>
                                </form>
                            </div>

                            {/* Select existing domain */}
                            <div className="rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm">
                                <div className="flex items-center gap-3 mb-5">
                                    <div className="rounded-2xl bg-amber-100 p-3 text-amber-600">
                                        <FaGlobe />
                                    </div>
                                    <div>
                                        <h2 className="font-semibold text-slate-900">Select an existing domain</h2>
                                        <p className="text-sm text-slate-500">Pick the domain for your mailboxes.</p>
                                    </div>
                                </div>

                                {loadingDomains ? (
                                    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                                        Loading domains...
                                    </div>
                                ) : domains.length === 0 ? (
                                    <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500">
                                        No domains yet — create one above first.
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                        {domains.map((domain) => (
                                            <button
                                                key={domain.id}
                                                type="button"
                                                onClick={() => setSelectedDomain(domain.domain)}
                                                className={`rounded-2xl border px-4 py-4 text-left transition ${
                                                    selectedDomain === domain.domain
                                                        ? "border-slate-900 bg-slate-900 text-white"
                                                        : "border-slate-200 bg-slate-50 text-slate-700 hover:border-slate-400"
                                                }`}
                                            >
                                                <div className="text-sm font-semibold">{domain.domain}</div>
                                                <div className={`mt-1 text-xs ${selectedDomain === domain.domain ? "text-slate-300" : "text-slate-500"}`}>
                                                    {domain.provider} • {domain.status}
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Step 1 → Next */}
                            <div className="flex justify-end">
                                <button
                                    type="button"
                                    onClick={goToStep2}
                                    disabled={!selectedDomain}
                                    className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-6 py-3 text-sm font-semibold text-white transition hover:bg-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    Next: Email accounts
                                    <FaArrowRight />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ── STEP 2: Email accounts ── */}
                    {currentStep === 2 && (
                        <div className="space-y-5">
                            {/* Selected domain badge */}
                            <div className="flex items-center gap-2 rounded-2xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-700">
                                <FaGlobe className="shrink-0" />
                                <span>Working on domain: <strong>{selectedDomain}</strong></span>
                            </div>

                            {/* Create email account */}
                            <div className="rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm">
                                <div className="flex items-center gap-3 mb-5">
                                    <div className="rounded-2xl bg-emerald-100 p-3 text-emerald-600">
                                        <FaPlus />
                                    </div>
                                    <div>
                                        <h2 className="font-semibold text-slate-900">Add a new email account</h2>
                                        <p className="text-sm text-slate-500">
                                            Preview: <span className="font-medium text-slate-700">{previewEmail}</span>
                                        </p>
                                    </div>
                                </div>

                                <form onSubmit={handleCreateEmailAccount} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    <FormInput label="Mailbox name" hint="Only the part before @.">
                                        <input
                                            type="text"
                                            name="mailbox_name"
                                            value={emailForm.mailbox_name}
                                            onChange={handleEmailFormChange}
                                            placeholder="sales"
                                            className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
                                        />
                                    </FormInput>
                                    <FormInput label="First name">
                                        <input
                                            type="text"
                                            name="first_name"
                                            value={emailForm.first_name}
                                            onChange={handleEmailFormChange}
                                            placeholder="Jane"
                                            className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
                                        />
                                    </FormInput>
                                    <FormInput label="Last name">
                                        <input
                                            type="text"
                                            name="last_name"
                                            value={emailForm.last_name}
                                            onChange={handleEmailFormChange}
                                            placeholder="Doe"
                                            className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
                                        />
                                    </FormInput>
                                    <FormInput label="Forwarding account">
                                        <select
                                            name="forwarding_account_id"
                                            value={emailForm.forwarding_account_id}
                                            onChange={handleEmailFormChange}
                                            disabled={loadingForwardingAccounts || forwardingAccounts.length === 0}
                                            className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 disabled:bg-slate-100"
                                        >
                                            {forwardingAccounts.length === 0 ? (
                                                <option value="">
                                                    {loadingForwardingAccounts ? "Loading..." : "No forwarding accounts found"}
                                                </option>
                                            ) : (
                                                forwardingAccounts.map((acc) => (
                                                    <option key={acc.id} value={acc.id}>
                                                        {acc.label}{acc.email ? ` (${acc.email})` : ""}
                                                    </option>
                                                ))
                                            )}
                                        </select>
                                    </FormInput>
                                    <FormInput label="Provider" hint="Defaults to maildoso.">
                                        <input
                                            type="text"
                                            name="provider"
                                            value={emailForm.provider}
                                            onChange={handleEmailFormChange}
                                            placeholder="maildoso"
                                            className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
                                        />
                                    </FormInput>
                                    <FormInput label="Picture URL" hint="Optional.">
                                        <input
                                            type="url"
                                            name="picture_url"
                                            value={emailForm.picture_url}
                                            onChange={handleEmailFormChange}
                                            placeholder="https://cdn.yourcompany.com/avatar.png"
                                            className="w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-slate-900 outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
                                        />
                                    </FormInput>
                                    <div className="sm:col-span-2 flex justify-end">
                                        <button
                                            type="submit"
                                            disabled={submittingEmail || forwardingAccounts.length === 0}
                                            className="inline-flex items-center gap-2 rounded-2xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-60"
                                        >
                                            <FaPlus />
                                            {submittingEmail ? "Creating..." : "Add email account"}
                                        </button>
                                    </div>
                                </form>
                            </div>

                            {/* Existing accounts list */}
                            <div className="rounded-[24px] border border-slate-200 bg-white p-6 shadow-sm">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                        <div className="rounded-2xl bg-indigo-100 p-3 text-indigo-600">
                                            <FaEnvelope />
                                        </div>
                                        <div>
                                            <h2 className="font-semibold text-slate-900">Email accounts for {selectedDomain}</h2>
                                            <p className="text-sm text-slate-500">{filteredEmailAccounts.length} account{filteredEmailAccounts.length === 1 ? "" : "s"} found</p>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => fetchAllEmailAccounts()}
                                        disabled={loadingEmailAccounts}
                                        className="rounded-xl border border-slate-200 p-2 text-slate-500 transition hover:bg-slate-50 disabled:opacity-50"
                                    >
                                        <FaRedo className={loadingEmailAccounts ? "animate-spin" : ""} />
                                    </button>
                                </div>

                                <div className="space-y-3">
                                    {loadingEmailAccounts ? (
                                        <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500">Loading...</div>
                                    ) : filteredEmailAccounts.length === 0 ? (
                                        <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-sm text-slate-500">No accounts yet — add one above.</div>
                                    ) : (
                                        filteredEmailAccounts.map((account) => (
                                            <div key={account.id} className="flex items-center justify-between gap-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                                                <div className="min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <FaEnvelope className="shrink-0 text-slate-400" />
                                                        <p className="truncate text-sm font-semibold text-slate-800">{account.email}</p>
                                                    </div>
                                                    <div className="mt-1 flex flex-wrap gap-2 text-xs text-slate-500">
                                                        <span className="rounded-full bg-white px-2 py-0.5 border border-slate-200">{account.provider}</span>
                                                        <span className="rounded-full bg-white px-2 py-0.5 border border-slate-200">{account.isActive ? "Active" : "Inactive"}</span>
                                                        <span
                                                            className={`rounded-full border px-2 py-0.5 font-semibold ${getGoogleScoreBadgeClass(account.googleScore)}`}
                                                        >
                                                            Reputation: {(account.googleScore || "unknown").toUpperCase()}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="shrink-0">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleDeleteEmailAccount(account.id, account.email)}
                                                        disabled={deletingAccountId === String(account.id)}
                                                        className="inline-flex items-center gap-1.5 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700 transition hover:bg-rose-100 disabled:opacity-60"
                                                    >
                                                        <FaTrash />
                                                        {deletingAccountId === String(account.id) ? "Deleting..." : "Delete"}
                                                    </button>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>

                            </div>

                            {/* Step 2 actions */}
                            <div className="flex justify-between">
                                <button
                                    type="button"
                                    onClick={() => { setActionMessage({ type: "", text: "" }); setCurrentStep(1); }}
                                    className="inline-flex items-center gap-2 rounded-2xl border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                                >
                                    <FaArrowLeft />
                                    Back
                                </button>
                                <button
                                    type="button"
                                    onClick={goToStep3}
                                    className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-6 py-3 text-sm font-semibold text-white transition hover:bg-slate-700"
                                >
                                    Next: Confirm
                                    <FaArrowRight />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* ── STEP 3: Confirmation ── */}
                    {currentStep === 3 && (
                        <div className="space-y-5">
                            <div className="rounded-[24px] border border-emerald-200 bg-emerald-50 p-8 text-center shadow-sm">
                                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
                                    <FaCheckCircle className="text-3xl text-emerald-600" />
                                </div>
                                <h2 className="text-xl font-semibold text-slate-900">You're all set!</h2>
                                <p className="mt-2 text-sm text-slate-600 max-w-sm mx-auto">
                                    Your domain <strong>{selectedDomain}</strong> and its email accounts have been created and integrated with unipile.
                                </p>

                                <div className="mt-6 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
                                    <button
                                        type="button"
                                        onClick={() => { setActionMessage({ type: "", text: "" }); setCurrentStep(2); }}
                                        className="inline-flex items-center gap-2 rounded-2xl border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                                    >
                                        <FaArrowLeft />
                                        <FaEnvelope />
                                        Back to email accounts
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => navigate("/dashboard")}
                                        className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-6 py-3 text-sm font-semibold text-white transition hover:bg-slate-700"
                                    >
                                        Dashboard
                                        <FaArrowRight />
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
};

export default DomainEmailPage;